import { ErrorLogComponent } from '@cian/error-log-component';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { CardSectionNew } from 'shared/components/CardSectionNew';
import { SimilarOffersAnchor } from 'shared/components/SimilarOffersSection';
import { useApplicationContext } from 'shared/containers/ApplicationContext';
import { DiscountMortgageLabelContainer } from 'shared/containers/DiscountMortgageLabelContainer';
import {
  EInfrastructureProfile,
  InfrastructureMicrofrontendContainer,
} from 'shared/containers/InfrastructureMicrofrontendContainer';
import { NearbyNewbuildingsLinkContainer } from 'shared/containers/NearbyNewbuildingsLinkContainer';
import { ReliabilityContainer } from 'shared/containers/Reliability';
import { SpecialPromoLinkContainer } from 'shared/containers/SpecialPromoLinkContainer';
import { TourContainer } from 'shared/containers/TourContainer';
import { useCardUxfeedbackCampaign } from 'shared/hooks/useCardUxfeedbackCampaign';

import { FeedbackPopupContainer as FeedBackPopupC } from '../../FeedbackExperiment/components/PopupWithComplaint/FeedbackPopup';
import { PopupFeedbackSentContainer } from '../../FeedbackExperiment/components/PopupWithForm/PopupFeedbackSent';
import { LabelsLayoutNew } from '../../components/LabelsLayout';
import { NewbuildingFeaturesSection } from '../../components/NewbuildingFeaturesSection';
import {
  OfferCardPageLayout,
  OfferCardPageLayoutAside,
  OfferCardPageLayoutCenter,
  OfferCardPageLayoutFooter,
  OfferCardPageLayoutTop,
} from '../../components/OfferCardPageLayout';
import { OfferHistoryContainer } from '../../components/OfferHistory/containers';
import { AdminButtonsContainer } from '../../components/admin_buttons/container';
import { HeaderAdminPanelContainer } from '../../components/header_admin_panel/container';
import { OfferBreadcrumbsContainer } from '../../components/offer_breadcrumbs/container';
import { OfferIsHiddenBlock } from '../../components/offer_hidden_block';
import { OfferRefusedContainer } from '../../components/offer_refused/container';
import { OfferUnpublishedContainer } from '../../components/offer_unpublished/container';
import { HideOfferTopPopup } from '../../components/popups/hide_offer_top_popup';
import { LegendModalContainer } from '../../components/popups/legend/container';
import { PrintModalContainer } from '../../components/popups/print/container';
import { RemoveModalContainer } from '../../components/popups/remove/container';
import { PrintInformationContainer } from '../../components/print_information/container';
import { PrintPhotoContainer } from '../../components/print_photo/container';
import { getOffer } from '../../selectors/newObject';
import { IState } from '../../store';
import { TgbBottomBannersContainer } from '../Adfox/TgbBottomBannersContainer';
import { AsideRedesignedContainer } from '../AsideRedesigned/AsideRedesigned';
import { BottomAuthorBlockContainer } from '../BottomAuthorBlockNew';
import { ChatPopupContainer } from '../ChatPopupContainer';
import { ComparisonNotificationLoadableContainer } from '../ComparisonNotificationContainer';
import { ComplaintModalContainer } from '../ComplaintModalContainer';
import { DescriptionContainer } from '../Description/Description';
import { FavoritesFoldersContainer } from '../FavoritesFoldersContainer';
import { FlatViewOrderTooltipContainer } from '../FlatViewOrderTooltip';
import { HeatIndexContainer } from '../HeatIndexContainer';
import { IdenticalOffersContainer } from '../IdenticalOffersContainer';
import { MainNewContainer } from '../MainContainer';
import { MortgageBrokerBannerContainer } from '../MortgageBrokerBannerContainer';
import { NavigationContainerNew } from '../NavigationNew';
import { NewbuildingActionsContainer } from '../NewbuildingActionsContainer';
import { NewbuildingAdvantages } from '../NewbuildingAdvantages';
import { NewbuildingBookedFromDeveloperModalContainer } from '../NewbuildingBookedFromDeveloperModalContainer';
import { NewbuildingBrokerCallbackModalContainer } from '../NewbuildingBrokerCallbackModalContainer';
import { NewbuildingBrokerFlatBannerContainer } from '../NewbuildingBrokerFlatBannerContainer';
import { NewbuildingBrokerModalsProvider } from '../NewbuildingBrokerModalsProvider';
import { NewbuildingBrokerOnboardingContainer } from '../NewbuildingBrokerOnboardingContainer';
import { NewbuildingBrokerResultModalContainer } from '../NewbuildingBrokerResultModal';
import { NewbuildingDecorationsContainer } from '../NewbuildingDecorations';
import { GalleryContainer } from '../NewbuildingGalleryContainer';
import { NewbuildingMortgageSectionLoadable } from '../NewbuildingMortgageSection';
import { NewbuildingPromosSectionContainer } from '../NewbuildingPromosSection';
import { NewbuildingSpecifications } from '../NewbuildingSpecifications';
import { NewbuildingStatsContainer } from '../NewbuildingStats/NewbuildingStats';
import { NewbuildingsFromDeveloperLazy } from '../NewbuildingsFromDeveloper';
import { NotAvailablePopupContainer } from '../NotAvailablePopupContainer';
import { ObjectFactoidsContainer } from '../ObjectFactoid/ObjectFactoid';
import { ObjectSummaryTitleContainer } from '../ObjectSummaryTitle/ObjectSummaryTitleContainer';
import { OfferBookingLabelContainer } from '../OfferBookingLabelContainer';
import { OfferMapContainer } from '../OfferMapContainer';
import { OfferMetaDataContainer } from '../OfferMetaData/OfferMetaData';
import { OfferSummaryInfoNewV2 } from '../OfferSummaryInfoNew';
import { OfferValuationContainer } from '../OfferValuation';
import { OnlyCianLabelNewContainer } from '../OnlyCianLabelContainer';
import { PhoneWarningModalContainer } from '../PhoneWarningModalContainer';
import { RosreestrFlatSaleContainerNew } from '../RosreestrFlatSaleContainer';
import { SeoLinksContainer } from '../SeoLinks/SeoLinksContainer';
import { SimilarOffersGroupsContainer } from '../SimilarOffersRedesigned';
import { UxfbCookieUpdater } from '../UxfbCookieUpdater';
import { VisitedOffersUpdater } from '../VisitedOffersUpdater';
import { WarningsContainer } from '../WarningsContainer';

const NewbuildingPageContainer = () => {
  const context = useApplicationContext();
  const hiddenObjectsLkUrl = context.config.get<string>('hiddenObjects.lkUrl') || '';
  const offer = useSelector(getOffer);
  const isPublished = offer.status === 'published';
  const isHiddenByUser = useSelector((state: IState) => state.offerData.isHiddenByUser);
  const isTopPopupOpened = useSelector((state: IState) => state.hideOffer.isTopPopupOpened);
  const hideOfferStatus = useSelector((state: IState) => state.hideOffer.status);
  const hideOfferErrorMessage = useSelector((state: IState) => state.hideOffer.errorMessage);

  useCardUxfeedbackCampaign('flat');

  return (
    <NewbuildingBrokerModalsProvider>
      <NavigationContainerNew />
      <OfferCardPageLayout>
        <FeedBackPopupC />

        <PopupFeedbackSentContainer />
        <FlatViewOrderTooltipContainer />
        <OfferCardPageLayoutTop>
          <OfferBreadcrumbsContainer />
          <OfferMetaDataContainer />
          <HeaderAdminPanelContainer />
          <OfferRefusedContainer config={context.config} />
          <OfferUnpublishedContainer />
          <AdminButtonsContainer />
          <ErrorLogComponent logger={context.logger}>
            <WarningsContainer />
          </ErrorLogComponent>
          {!isPublished && <SimilarOffersGroupsContainer />}
        </OfferCardPageLayoutTop>

        {isTopPopupOpened && (
          <HideOfferTopPopup
            status={hideOfferStatus}
            hidden={Boolean(isHiddenByUser)}
            lkUrl={hiddenObjectsLkUrl}
            hideOfferErrorMessage={hideOfferErrorMessage}
          />
        )}

        {isHiddenByUser && <OfferIsHiddenBlock lkUrl={hiddenObjectsLkUrl} />}

        <OfferCardPageLayoutCenter>
          <ErrorLogComponent logger={context.logger}>
            <LabelsLayoutNew>
              <DiscountMortgageLabelContainer />
              <OnlyCianLabelNewContainer />
              <OfferBookingLabelContainer />
            </LabelsLayoutNew>
            <MainNewContainer />
          </ErrorLogComponent>
          {isPublished && (
            <>
              <GalleryContainer reactErrorLogger={context.logger} />
              <PrintPhotoContainer />
            </>
          )}

          <ObjectSummaryTitleContainer />
          <ObjectFactoidsContainer />
          <DescriptionContainer />
          <OfferSummaryInfoNewV2 />
          <RosreestrFlatSaleContainerNew />
          <NewbuildingActionsContainer />
          <NewbuildingDecorationsContainer />
          <TourContainer />
          {isPublished && [
            <div key="published-data">
              <NewbuildingFeaturesSection>
                <NewbuildingSpecifications />
                <NewbuildingBrokerFlatBannerContainer />
                <NewbuildingAdvantages />
              </NewbuildingFeaturesSection>
              <NewbuildingPromosSectionContainer />
              <NewbuildingMortgageSectionLoadable header="Ипотечные предложения" showHeader={true} />
              <MortgageBrokerBannerContainer />
              <ErrorLogComponent logger={context.logger}>
                <OfferMapContainer />
              </ErrorLogComponent>
              <ErrorLogComponent logger={context.logger}>
                <InfrastructureMicrofrontendContainer
                  wrapped
                  profile={EInfrastructureProfile.Newbuilding}
                  showOfferMarker={false}
                  headerLinks={
                    <>
                      <SpecialPromoLinkContainer />
                      <NearbyNewbuildingsLinkContainer />
                    </>
                  }
                />
              </ErrorLogComponent>
              <ReliabilityContainer />
              <NewbuildingStatsContainer />
              <HeatIndexContainer />
              <OfferValuationContainer />
              <OfferHistoryContainer />
              <ErrorLogComponent logger={context.logger}>
                <CardSectionNew hideForPrint>
                  <BottomAuthorBlockContainer />
                </CardSectionNew>
              </ErrorLogComponent>
            </div>,
          ]}
        </OfferCardPageLayoutCenter>

        <OfferCardPageLayoutAside>
          <AsideRedesignedContainer />
        </OfferCardPageLayoutAside>
      </OfferCardPageLayout>
      <OfferCardPageLayoutFooter>
        <NewbuildingsFromDeveloperLazy />
        <IdenticalOffersContainer />
        {isPublished && (
          <SimilarOffersAnchor>
            <SimilarOffersGroupsContainer />
          </SimilarOffersAnchor>
        )}
        <SeoLinksContainer />
        <TgbBottomBannersContainer />
      </OfferCardPageLayoutFooter>
      <PrintModalContainer />
      <PrintInformationContainer />
      <LegendModalContainer />
      <RemoveModalContainer />
      <ChatPopupContainer />
      <NotAvailablePopupContainer />
      <FavoritesFoldersContainer />
      <NewbuildingBookedFromDeveloperModalContainer />
      <NewbuildingBrokerCallbackModalContainer />
      <NewbuildingBrokerResultModalContainer />
      <NewbuildingBrokerOnboardingContainer />
      <PhoneWarningModalContainer />
      <ComparisonNotificationLoadableContainer />
      <VisitedOffersUpdater />
      <UxfbCookieUpdater />
      <ComplaintModalContainer />
    </NewbuildingBrokerModalsProvider>
  );
};

// eslint-disable-next-line import-x/no-default-export
export default NewbuildingPageContainer;
